import encounterNoteService from '@/services/encounternoteService.js';

/**
 * Create a new encounter note
 * @param {Object} data - Note data
 * @returns {Promise} API response
 */
export const createEncounterNoteApi = async (data) => {
  try {
    const response = await encounterNoteService.post('/encounter-notes', data);
    return response.data;
  } catch (error) {
    console.error('Failed to create encounter note:', error);
    throw error;
  }
};

/**
 * Auto-save draft encounter note
 * @param {string} id - Note ID
 * @param {Object} data - Note data (content, content_version, etc.)
 * @returns {Promise} API response
 */
export const autoSaveEncounterNoteApi = async (id, data) => {
  try {
    const response = await encounterNoteService.patch(`/encounter-notes/${id}/auto-save`, data);
    return response.data;
  } catch (error) {
    console.error('Failed to auto-save encounter note:', error);
    throw error;
  }
};

/**
 * Update an existing encounter note
 * @param {string} id - Note ID
 * @param {Object} data - Note data to update
 * @returns {Promise} API response
 */
export const updateEncounterNoteApi = async (id, data) => {
  try {
    const response = await encounterNoteService.put(`/encounter-notes/${id}`, data);
    return response.data;
  } catch (error) {
    console.error('Failed to update encounter note:', error);
    throw error;
  }
};

/**
 * Get an encounter note by ID
 * @param {string} id - Note ID
 * @returns {Promise} API response
 */
export const getEncounterNoteApi = async (id) => {
  try {
    const response = await encounterNoteService.get(`/encounter-notes/${id}`);
    return response.data;
  } catch (error) {
    console.error('Failed to get encounter note:', error);
    throw error;
  }
};

/**
 * Delete an encounter note
 * @param {string} id - Note ID
 * @returns {Promise} API response
 */
export const deleteEncounterNoteApi = async (id) => {
  try {
    const response = await encounterNoteService.delete(`/encounter-notes/${id}`);
    return response.data;
  } catch (error) {
    console.error('Failed to delete encounter note:', error);
    throw error;
  }
};

/**
 * Get a list of encounter notes
 * @param {Object} params - Query parameters
 * @param {string} [params.patient_id] - Patient ID (comma-separated allowed)
 * @param {string[]} [params.patient_ids] - Array of patient IDs
 * @param {string} [params.service_date_from] - ISO date string
 * @param {string} [params.service_date_to] - ISO date string
 * @param {string} [params.appointment_type] - Appointment type filter
 * @param {string} [params.visit_type] - Visit type filter
 * @param {string} [params.status] - Status filter
 * @param {string} [params.sort] - Sort field (service_date|created_at|updated_at)
 * @param {string} [params.order] - Sort order (asc|desc)
 * @param {number} [params.limit] - Number of results to return
 * @param {number} [params.offset] - Offset for pagination
 * @returns {Promise} API response with { notes, total, limit, offset }
 */
export const getEncounterNotesListApi = async (params = {}) => {
  try {
    const response = await encounterNoteService.get('/encounter-notes', { params });
    return response.data;
  } catch (error) {
    console.error('Failed to get encounter notes list:', error);
    throw error;
  }
};

/**
 * Upload a provider signature image
 * @param {string} customerId - Customer/Provider ID
 * @param {File|Blob} file - Signature image file (binary, required)
 * @returns {Promise} API response with signature details
 */
export const uploadSignatureApi = async (customerId, file) => {
  try {
    const formData = new FormData();
    formData.append('file', file, 'signature.png');

    const response = await encounterNoteService.post(`/signatures/customers/${customerId}`, formData);
    return response.data;
  } catch (error) {
    console.error('Failed to upload signature:', error);
    throw error;
  }
};

/**
 * Get the latest signature ID for a customer
 * @param {string} customerId - Customer/Provider ID
 * @returns {Promise} API response with { signature_id }
 */
export const getLatestSignatureIdApi = async (customerId) => {
  try {
    const response = await encounterNoteService.get(`/signatures/latest/${customerId}`);
    return response.data;
  } catch (error) {
    // 404 means no signature exists yet, which is a valid state
    if (error.response?.status === 404) {
      return null;
    }
    console.error('Failed to get latest signature ID:', error);
    throw error;
  }
};

/**
 * Get signature image details by ID
 * @param {string} signatureId - Signature ID
 * @returns {Promise} API response with signature details including download_url
 */
export const getSignatureApi = async (signatureId) => {
  try {
    const response = await encounterNoteService.get(`/signatures/${signatureId}`);
    return response.data;
  } catch (error) {
    console.error('Failed to get signature:', error);
    throw error;
  }
};

/**
 * Sign an encounter note with a signature
 * @param {string} noteId - Encounter note ID
 * @param {string} signatureId - Signature ID to use for signing
 * @returns {Promise} API response
 */
export const signEncounterNoteApi = async (noteId, signatureId) => {
  try {
    const response = await encounterNoteService.post(`/encounter-notes/${noteId}/sign`, {
      signature_id: signatureId,
    });
    return response.data;
  } catch (error) {
    console.error('Failed to sign encounter note:', error);
    throw error;
  }
};

/**
 * Get the edit lock status of an encounter note
 * @param {string} id - Encounter note ID
 * @returns {Promise} API response with { locked, holder? }
 */
export const getEditLockStatusApi = async (id) => {
  try {
    const response = await encounterNoteService.get(`/encounter-notes/${id}/edit-lock-status`);
    return response.data;
  } catch (error) {
    console.error('Failed to get edit lock status:', error);
    throw error;
  }
};

/**
 * Acquire the edit lock for an encounter note
 * @param {string} id - Encounter note ID
 * @returns {Promise} API response
 */
export const acquireEditLockApi = async (id) => {
  try {
    const response = await encounterNoteService.post(`/encounter-notes/${id}/acquire-edit-lock`);
    return response.data;
  } catch (error) {
    console.error('Failed to acquire edit lock:', error);
    throw error;
  }
};

/**
 * Release the edit lock for an encounter note
 * @param {string} id - Encounter note ID
 * @returns {Promise} API response
 */
export const releaseEditLockApi = async (id) => {
  try {
    const response = await encounterNoteService.post(`/encounter-notes/${id}/release-edit-lock`);
    return response.data;
  } catch (error) {
    console.error('Failed to release edit lock:', error);
    throw error;
  }
};

/**
 * Get patient match data for 3-letter auto-match
 * @param {string} patientId - Patient ID
 * @returns {Promise} API response with match data
 */
export const getPatientMatchApi = async (patientId) => {
  try {
    const response = await encounterNoteService.get('/match/patient', {
      params: { patient_id: patientId },
    });
    return response.data;
  } catch (error) {
    console.error('Failed to get patient match data:', error);
    throw error;
  }
};

/**
 * Get patient variables for $ trigger
 * @param {string} patientId - Patient ID
 * @returns {Promise} API response with { patient_variables: [{ name, value }] }
 */
export const getPatientVariablesApi = async (patientId) => {
  try {
    const response = await encounterNoteService.get('/match/patient/variables', {
      params: { patient_id: patientId },
    });
    return response.data;
  } catch (error) {
    console.error('Failed to get patient variables:', error);
    throw error;
  }
};

/**
 * Get inline text for action cards
 * @param {string} type - Action card type (appointment|task|order|document)
 * @returns {Promise} API response
 */
export const getActionCardsInlineTextApi = async (type) => {
  try {
    const response = await encounterNoteService.get('/encounter-notes/action-cards/inline-text', {
      params: { type },
    });
    return response.data;
  } catch (error) {
    console.error('Failed to get action cards inline text:', error);
    throw error;
  }
};
